import React from "react";
import {
  FileCode,
  FilePlus,
  FileX,
  ChevronDown,
  ChevronRight,
  Loader2,
  Check,
  X,
  Plus,
  Minus,
} from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { isProtectedEntryPath } from "@/lib/patchGuard";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";

interface FileEdit {
  filePath: string;
  action: "create" | "modify" | "delete";
  content?: string;
  explanation: string;
}

/** Simple line diff: common prefix/suffix, middle chunk as removed + added. */
function simpleLineDiff(
  oldStr: string,
  newStr: string
): { added: number; removed: number; lines: { type: "add" | "del"; text: string }[] } {
  const oldLines = (oldStr ?? "").split("\n");
  const newLines = (newStr ?? "").split("\n");
  let i = 0;
  while (i < oldLines.length && i < newLines.length && oldLines[i] === newLines[i]) i++;
  let j = oldLines.length - 1;
  let k = newLines.length - 1;
  while (j >= i && k >= i && oldLines[j] === newLines[k]) {
    j--;
    k--;
  }
  const removed = Math.max(0, j - i + 1);
  const added = Math.max(0, k - i + 1);
  const lines: { type: "add" | "del"; text: string }[] = [];
  for (let r = i; r <= j; r++) lines.push({ type: "del", text: oldLines[r] });
  for (let a = i; a <= k; a++) lines.push({ type: "add", text: newLines[a] });
  return { added, removed, lines };
}

/**
 * IMPORTANT:
 * Backend responses may not always include edits / reasoning.
 * Keep plan fields optional to prevent UI crashes.
 */
interface EditPlan {
  summary?: string;
  edits?: FileEdit[];
  reasoning?: string;
  // allow future/backward-compat fields without breaking
  [key: string]: any;
}

interface ApplyResult {
  filePath: string;
  action: string;
  success: boolean;
  error?: string;
}

interface AIEditPlanDisplayProps {
  plan: EditPlan;
  onApply?: (
    edits: FileEdit[]
  ) => Promise<{ success: boolean; summary: string; results: ApplyResult[] }>;
  isApplying?: boolean;
  /** Current file contents (path -> content) for diff preview. If missing, only action + content preview is shown. */
  currentFileContents?: Record<string, string> | null;
  /** Called when user clicks Cancel; use to collapse plan and focus prompt input. */
  onCancel?: () => void;
}

const ActionIcon = ({ action }: { action: string }) => {
  switch (action) {
    case "create":
      return <FilePlus className="h-4 w-4 text-green-500" />;
    case "modify":
      return <FileCode className="h-4 w-4 text-amber-500" />;
    case "delete":
      return <FileX className="h-4 w-4 text-red-500" />;
    default:
      return <FileCode className="h-4 w-4" />;
  }
};

const FileEditItem = ({
  edit,
  result,
  currentContent,
}: {
  edit: FileEdit;
  result?: ApplyResult;
  currentContent?: string;
}) => {
  const [expanded, setExpanded] = useState(false);

  const { addCount, delCount, diffLines } = useMemo(() => {
    const oldContent = currentContent ?? "";
    const newContent = edit.action === "delete" ? "" : (edit.content ?? "");
    if (edit.action === "create") {
      const lines = (edit.content ?? "").split("\n").length;
      return { addCount: lines, delCount: 0, diffLines: [] as { type: "add" | "del"; text: string }[] };
    }
    if (edit.action === "delete") {
      const lines = oldContent.split("\n").length;
      return { addCount: 0, delCount: lines, diffLines: oldContent ? oldContent.split("\n").map((t) => ({ type: "del" as const, text: t })) : [] };
    }
    const diff = simpleLineDiff(oldContent, newContent);
    return { addCount: diff.added, delCount: diff.removed, diffLines: diff.lines };
  }, [edit.action, edit.content, currentContent]);

  const hasDiff = diffLines.length > 0 || (edit.action === "create" && (edit.content ?? "").length > 0);

  return (
    <div className="border border-border rounded-md overflow-hidden bg-background/50">
      <button
        type="button"
        className="w-full flex items-center gap-2 p-2 hover:bg-muted/50 text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        <ActionIcon action={edit.action} />
        <span className="text-sm font-mono truncate flex-1 min-w-0">{edit.filePath}</span>

        {result ? (
          result.success ? (
            <Check className="h-4 w-4 text-green-500 shrink-0" />
          ) : (
            <X className="h-4 w-4 text-red-500 shrink-0" />
          )
        ) : (
          <div className="flex items-center gap-1.5 shrink-0">
            {addCount > 0 && (
              <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-0.5">
                <Plus className="h-3 w-3" />
                {addCount}
              </span>
            )}
            {delCount > 0 && (
              <span className="text-xs text-red-600 dark:text-red-400 flex items-center gap-0.5">
                <Minus className="h-3 w-3" />
                {delCount}
              </span>
            )}
            {addCount === 0 && delCount === 0 && (
              <span
                className={cn(
                  "text-xs px-1.5 py-0.5 rounded",
                  edit.action === "create" && "bg-green-500/20 text-green-400",
                  edit.action === "modify" && "bg-amber-500/20 text-amber-400",
                  edit.action === "delete" && "bg-red-500/20 text-red-400"
                )}
              >
                {edit.action}
              </span>
            )}
          </div>
        )}
      </button>

      {expanded && (
        <div className="border-t border-border">
          <div className="p-2 bg-muted/30">
            <p className="text-xs text-muted-foreground mb-2">
              {edit.explanation}
            </p>
            {result?.error && (
              <p className="text-xs text-red-400 mb-2">
                Error: {result.error}
              </p>
            )}
            {hasDiff && (
              <ScrollArea className="max-h-48 rounded border border-border/50 bg-zinc-900/80">
                <div className="p-2 font-mono text-xs">
                  {diffLines.length > 0 ? (
                    diffLines.map((line, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          "whitespace-pre truncate max-w-full",
                          line.type === "add" && "text-green-400 bg-green-500/10",
                          line.type === "del" && "text-red-400 bg-red-500/10"
                        )}
                      >
                        {line.type === "del" ? "- " : "+ "}
                        <span className="truncate inline-block max-w-full align-bottom" title={line.text}>
                          {line.text || " "}
                        </span>
                      </div>
                    ))
                  ) : (
                    <pre className="text-green-400/90 whitespace-pre-wrap break-words">
                      {edit.content}
                    </pre>
                  )}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export function AIEditPlanDisplay({
  plan,
  onApply,
  isApplying = false,
  currentFileContents = null,
  onCancel,
}: AIEditPlanDisplayProps) {
  // Normalize everything so this component never crashes
  const summary = typeof plan?.summary === "string" ? plan.summary : "No summary returned.";
  const reasoning = typeof plan?.reasoning === "string" ? plan.reasoning : "";

  // Always use a safe edits array (never touch plan.edits directly in render)
  // Filter out protected entry files (index.html, src/main.tsx) from display
  const edits: FileEdit[] = useMemo(() => {
    let raw: FileEdit[] = [];
    if (Array.isArray(plan?.edits)) raw = plan.edits;
    else if (Array.isArray((plan as any)?.patches)) raw = (plan as any).patches;
    return raw.filter((e) => !isProtectedEntryPath(e.filePath ?? (e as any).path ?? ""));
  }, [plan]);

  const [showReasoning, setShowReasoning] = useState(false);
  const [applied, setApplied] = useState(false);
  const [results, setResults] = useState<ApplyResult[]>([]);

  const getCurrentContent = (filePath: string) => currentFileContents?.[filePath];

  const handleApply = async () => {
    // Hard guards: never call onApply with undefined edits, never double-apply
    if (!onApply || isApplying || applied) return;

    try {
      const response = await onApply(edits);
      setResults(Array.isArray(response?.results) ? response.results : []);
      setApplied(true);
    } catch (e) {
      // Don’t crash the app if apply fails
      console.error("Apply failed:", e);
      setResults([{ filePath: "(apply)", action: "apply", success: false, error: String(e) }]);
      setApplied(true);
    }
  };

  const getResultForEdit = (edit: FileEdit) => {
    return results.find((r) => r.filePath === edit.filePath);
  };

  const successCount = results.filter((r) => r.success).length;
  const failCount = results.filter((r) => !r.success).length;

  return (
    <div className="space-y-3">
      <div className="bg-primary/10 rounded-lg p-3">
        <p className="text-sm font-medium">{summary}</p>

        <p className="text-xs text-muted-foreground mt-1">
          {applied ? (
            <>
              <span className="text-green-400">{successCount} succeeded</span>
              {failCount > 0 && (
                <span className="text-red-400">, {failCount} failed</span>
              )}
            </>
          ) : (
            <>
              {edits.length} file{edits.length !== 1 ? "s" : ""} will be affected • review diff below
            </>
          )}
        </p>
      </div>

      {reasoning && (
        <div>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            onClick={() => setShowReasoning((v) => !v)}
          >
            {showReasoning ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            View reasoning
          </button>

          {showReasoning && (
            <p className="text-xs text-muted-foreground mt-2 p-2 bg-muted/30 rounded">
              {reasoning}
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase">
          {applied ? "Applied Changes" : "Proposed Changes (expand to see diff)"}
        </p>

        {edits.length === 0 ? (
          <div className="text-xs text-muted-foreground p-3 rounded bg-muted/30">
            No file edits were generated.
          </div>
        ) : (
          edits.map((edit, index) => (
            <FileEditItem
              key={`${edit.filePath}-${index}`}
              edit={edit}
              result={getResultForEdit(edit)}
              currentContent={getCurrentContent(edit.filePath)}
            />
          ))
        )}
      </div>

      {!applied && edits.length > 0 && (
        <div className="flex gap-2 pt-2">
          <Button
            onClick={handleApply}
            disabled={!onApply || isApplying}
            className="flex-1 text-xs"
            size="sm"
          >
            {isApplying ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                Applying...
              </>
            ) : (
              "Apply changes"
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => onCancel?.()}
            disabled={isApplying}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
